import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ServiceFeaturedController } from "@/components/pages/guesteria-partners/hooks/useServiceFeatured";

export interface ServiceFeaturedSwitchProps {
  /** Id of the hotel service; `null` or missing: the switch is unavailable. */
  serviceId: string | null | undefined;
  featured: ServiceFeaturedController;
  /** Why the caller does not allow the switch (shown as a hint). */
  unavailableReason?: string | null;
}

export const ServiceFeaturedSwitch = ({
  serviceId,
  featured,
  unavailableReason = null,
}: ServiceFeaturedSwitchProps): React.JSX.Element => {
  const { t } = useTranslation();
  // Value the user asked for while the request runs; `null` otherwise.
  const [requested, setRequested] = useState<boolean | null>(null);

  const id = serviceId ?? null;
  let reason = unavailableReason;
  if (reason === null && id === null) {
    reason = t("partners.curation.featured.notPublished");
  }
  if (reason === null && !featured.isReady) {
    reason = featured.loadFailed
      ? t("partners.curation.featured.settingsLoadError")
      : t("partners.curation.featured.settingsLoading");
  }
  const disabled = reason !== null || requested !== null;
  const serverChecked = id !== null && featured.featuredIds.has(id);

  const handleChange = async (checked: boolean) => {
    if (id === null || disabled) return;
    setRequested(checked);
    try {
      await featured.setFeatured(id, checked);
    } catch {
      toast.error(t("partners.curation.featured.saveError"));
    } finally {
      setRequested(null);
    }
  };

  const control = (
    <Switch
      checked={requested ?? serverChecked}
      disabled={disabled}
      onCheckedChange={handleChange}
      aria-label={t("partners.curation.featured.label")}
    />
  );

  if (reason === null) {
    return control;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {control}
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
};
